/**
 * UndoButton Component
 *
 * Reusable undo button for all simulation pages.
 * Displays undo count and handles click events.
 *
 * @module components/simulation/components/common/UndoButton
 *
 * Props:
 * @param {number} undoCount - Number of available undo operations
 * @param {function} onUndo - Callback when undo is clicked
 * @param {boolean} disabled - Whether the button is disabled
 * @param {string} className - Additional CSS classes
 * @param {boolean} showIcon - Whether to show the undo icon (default: true)
 * @param {string} position - Button position: 'left' or 'right' (default: 'right')
 *
 * Usage:
 * <UndoButton
 *   undoCount={undoStack.length}
 *   onUndo={handleUndo}
 *   disabled={isImpactedMaster}
 * />
 */

import React from 'react';
import PropTypes from 'prop-types';
import Button from '../../../layout/Button';

const UndoButton = ({
    undoCount,
    onUndo,
    disabled = false,
    className = '',
    showIcon = true,
    position = 'right'
}) => {
    // Don't render if no undo available and disabled
    if (undoCount === 0 && disabled) {
        return null;
    }

    const floatClass = position === 'right' ? 'float-right mr-2' : 'mr-2';
    const buttonClass = `user-btn ${floatClass} ${className}`.trim();

    const title = disabled
        ? 'Undo is not available'
        : `Undo last change (Ctrl+Z) - ${undoCount} change${undoCount !== 1 ? 's' : ''} available`;

    return (
        <Button
            className={buttonClass}
            title={title}
            onClick={onUndo}
            disabled={disabled || undoCount === 0}
        >
            {showIcon && <i className="fa fa-undo mr-1"></i>}
            Undo ({undoCount})
        </Button>
    );
};

UndoButton.propTypes = {
    undoCount: PropTypes.number.isRequired,
    onUndo: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    showIcon: PropTypes.bool,
    position: PropTypes.oneOf(['left', 'right'])
};

export default UndoButton;
